import { DateTime } from "luxon";

import styled from "@mui/material/styles/styled";
import Typography from "@mui/material/Typography";

import { roundedOutline } from "@readai/common/components/styles";
import { Row } from "@readai/pulse/components/Stack";

import { RecommendedTimeIndicator } from "@app/booking/components/styles";

const getFormattedDate = (date: DateTime) =>
  `${date.toLocaleString({ weekday: "short" })} ${date.toLocaleString({
    month: "numeric",
    day: "numeric",
  })}`;

const getFormattedTime = (time: DateTime) =>
  time.toLocaleString({
    hour: "numeric",
    minute: "2-digit",
  });

const Container = styled(Row)(({ theme }) => ({
  position: "relative",
  border: `1px solid ${theme.palette.shades.neutral[100]}`,
  padding: theme.spacing(1, 2),
  borderRadius: "6px",
  transition: "all 150ms ease",
  backgroundColor: "#FFF",
  "&.recommended": {
    backgroundColor: theme.palette.shades.meadow[50],
  },
  "&:hover": {
    borderColor: theme.palette.shades.primary[500],
    cursor: "pointer",
  },
  "&.selected": {
    backgroundColor: theme.palette.shades.ghost[500],
    borderColor: theme.palette.shades.primary[500],
    ...roundedOutline({
      borderRadius: "5px",
      border: `1px solid ${theme.palette.shades.primary[500]}`,
    }),
  },
}));

const Text = styled(Typography)(({ theme }) => ({
  fontSize: "1.125rem",
  color: theme.palette.shades.neutral[800],
}));

const getClassNameFromProps = ({
  recommended,
  selected,
}: {
  recommended: boolean;
  selected: boolean;
}) => {
  // eslint-disable-next-line lingui/no-unlocalized-strings
  if (recommended && selected) return "recommended selected";
  if (selected) return "selected";
  if (recommended) return "recommended";
};

const TimeSlot = ({
  includeDate = false,
  onClick,
  recommended = false,
  selected = false,
  time,
}: {
  includeDate?: boolean;
  onClick: (time: DateTime) => void;
  recommended?: boolean;
  selected?: boolean;
  time: DateTime;
}) => {
  const handleClick = () => onClick(time);

  return (
    <Container
      variant="apart"
      className={getClassNameFromProps({ recommended, selected })}
      spacing={2}
      onClick={handleClick}
    >
      <Row variant="left" spacing={2}>
        {includeDate && <Text>{getFormattedDate(time)}</Text>}
        <Text>{getFormattedTime(time)}</Text>
      </Row>
      {recommended && <RecommendedTimeIndicator />}
    </Container>
  );
};

export default TimeSlot;
